import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import paymentMethodService from '../services/paymentMethodService';
import corporateProfileService from '../services/corporateProfileService';
import OhipLovPicker from './OhipAgentTypePicker';
import './AddCorporateProfilePage.css';

const EMPTY_FORM = {
    code: '',
    description: '',
    active: true,
};

const AddPaymentMethodPage = () => {
    const navigate = useNavigate();
    const { ppId } = useParams();
    const [searchParams] = useSearchParams();
    const ruleId = searchParams.get('ruleId');
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const set = (field, value) => {
        setForm(f => ({ ...f, [field]: value }));
        setErrors(e => ({ ...e, [field]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.code.trim()) e.code = 'Code is required';
        return e;
    };

    const handleCreate = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setSaving(true);
        try {
            await paymentMethodService.create(ppId, {
                code:        form.code.trim(),
                description: form.description || null,
                active:      form.active,
            }, ruleId);
            navigate(ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}` : `/apps/payment-porter/${ppId}`);
        } catch (err) {
            setErrors({ submit: err.response?.data?.error?.message || 'Failed to save payment method' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="acp-page">
            <div className="acp-card">
                <div className="acp-card-header">
                    <h1 className="acp-title">Add Payment Method</h1>
                </div>

                <div className="acp-divider" />

                <div className="acp-body">
                    <div className="acp-row-2">
                        <div className="acp-field">
                            <label className="acp-label">Payment Method Code <span className="acp-required">*</span></label>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                <input
                                    className={`acp-input${errors.code ? ' acp-input-error' : ''}`}
                                    type="text"
                                    placeholder="e.g., VCC, CA, VI"
                                    value={form.code}
                                    onChange={e => set('code', e.target.value)}
                                    autoFocus
                                />
                                <OhipLovPicker
                                    fetchFn={() => corporateProfileService.getOhipPaymentMethods(ppId)}
                                    title="Select Payment Method from OHIP"
                                    emptyMsg="No payment methods returned from OHIP."
                                    onSelectItem={({ code, description }) => {
                                        set('code', code);
                                        if (description && description !== code) set('description', description);
                                    }}
                                />
                            </div>
                            {errors.code && <span className="acp-error-msg">{errors.code}</span>}
                        </div>

                        <div className="acp-field" style={{ alignSelf: 'center' }}>
                            <label className="acp-checkbox-label" style={{ marginTop: '1.5rem' }}>
                                <input
                                    type="checkbox"
                                    className="acp-checkbox"
                                    checked={form.active}
                                    onChange={e => set('active', e.target.checked)}
                                />
                                Active
                            </label>
                        </div>
                    </div>

                    <div className="acp-field">
                        <label className="acp-label">Description</label>
                        <textarea
                            className="acp-input"
                            rows={4}
                            placeholder="Describe the payment method and its use cases"
                            value={form.description}
                            onChange={e => set('description', e.target.value)}
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                </div>

                {errors.submit && <div className="acp-error-msg" style={{ margin: '0 0 1rem' }}>{errors.submit}</div>}
                <div className="acp-footer">
                    <button
                        className="acp-btn-cancel"
                        onClick={() => navigate(ruleId ? `/apps/payment-porter/${ppId}/rules/${ruleId}` : `/apps/payment-porter/${ppId}`)}
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        className="acp-btn-create"
                        onClick={handleCreate}
                        disabled={saving}
                    >
                        {saving ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddPaymentMethodPage;
